import { http } from "../httpHandler";
import {
  CreateDeliverableRequestPayload,
  Deliverable,
  DeliverableRevisionPayload,
  DeliverableListItem,
  DeliverableListParams,
  DeliverableBrandingDefaults,
  DeliverableSourceRecord,
  SyncDeliverableAssetsPayload,
  SyncDeliverableSourcesPayload,
  TServerErrorResponse,
  UpdateDeliverablePayload,
  hasDeliverableArtifact,
} from "@/types";
import { links } from "@/constants";

export const DELIVERABLE_LIST = "DELIVERABLE_LIST";
export const DELIVERABLE_GET = "DELIVERABLE_GET";
export const DELIVERABLE_SOURCES = "DELIVERABLE_SOURCES";
export const DELIVERABLE_BRANDING_DEFAULTS = "DELIVERABLE_BRANDING_DEFAULTS";

/**
 * `GET /deliverable/branding/defaults` — firm-level logo filename and color
 * defaults shown in the new-deliverable composer before any override.
 */
export const getDeliverableBrandingDefaults =
  async (): Promise<DeliverableBrandingDefaults> => {
    try {
      const response = await http.get<DeliverableBrandingDefaults>(
        links.deliverable.brandingDefaults,
      );
      return response.data;
    } catch (error) {
      throw error as TServerErrorResponse;
    }
  };

/**
 * `GET /deliverable` — flat, name-searchable list of the acting user's own
 * deliverables (`scope: "mine"`) or the ones shared with them
 * (`scope: "shared"`). Params are shaped for `InfinityTable`'s pagination.
 */
export const getDeliverables = async (
  params?: DeliverableListParams,
): Promise<DeliverableListItem[]> => {
  try {
    const response = await http.get<DeliverableListItem[]>(
      links.deliverable.index,
      { params },
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

/** `POST /deliverable` — creates the focalmonolith row that owns build state. */
export const createDeliverable = async (
  data: CreateDeliverableRequestPayload,
): Promise<Deliverable> => {
  try {
    const response = await http.post<Deliverable>(
      links.deliverable.index,
      data,
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

/** `GET /deliverable/:id` — the full record at its current immutable revision. */
export const getDeliverableById = async (id: string): Promise<Deliverable> => {
  try {
    const response = await http.get<Deliverable>(links.deliverable.byId(id));
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

/** `PATCH /deliverable/:id` — update metadata only. */
export const updateDeliverable = async (
  id: string,
  data: UpdateDeliverablePayload,
): Promise<Deliverable> => {
  try {
    const response = await http.patch<Deliverable>(
      links.deliverable.byId(id),
      data,
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

/** `DELETE /deliverable/:id` — owner only. */
export const deleteDeliverable = async (id: string): Promise<void> => {
  try {
    await http.delete(links.deliverable.byId(id));
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

/**
 * `POST /deliverable/:id/rebuild` — resets a `FAILED` row back to
 * `GENERATING`. The build itself must then be re-enqueued through
 * `enqueueDeliverableBuild`; the monolith never contacts agent-service.
 */
export const rebuildDeliverable = async (id: string): Promise<Deliverable> => {
  try {
    const response = await http.post<Deliverable>(
      links.deliverable.rebuild(id),
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

/**
 * `PUT /deliverable/:id/figure` — replaces the whole figure set after a chat
 * edit. The backend flips the deliverable back to `DRAFT` if any figure is
 * `UNSOURCED`, so callers should refresh the deliverable afterwards.
 */
export const appendDeliverableRevision = async (
  id: string,
  data: DeliverableRevisionPayload,
): Promise<Deliverable> => {
  try {
    const response = await http.post<Deliverable>(
      links.deliverable.revisions(id),
      data,
    );
    return response.data;
  } catch (error) {
    // The request may have committed even when its response was interrupted.
    // Reconcile only a newer revision that either matches the artifact exactly
    // or records the same immutable parent, so validation failures still reach
    // the caller while harmless server-side HTML normalization is tolerated.
    const parentRevisionId = data.artifactMetadata.parentRevisionId;
    if (typeof parentRevisionId === "string") {
      try {
        const current = await getDeliverableById(id);
        const committedFromParent =
          hasDeliverableArtifact(current) &&
          current.artifactMetadata.parentRevisionId === parentRevisionId;
        if (
          hasDeliverableArtifact(current) &&
          current.currentRevisionId !== parentRevisionId &&
          (current.html === data.html || committedFromParent)
        ) {
          return current;
        }
      } catch {
        // Preserve the original revision-write error below.
      }
    }
    throw error as TServerErrorResponse;
  }
};

/**
 * `GET /deliverable/:id/source` — the stored corpus **with** its extracted text.
 *
 * The expensive read, deliberately on its own route: `GET /deliverable/:id`
 * already carries the same sources as summaries. Call this only when something
 * is about to use the text — a chat turn or a regeneration — and hold the
 * result for the life of the page rather than re-fetching it per turn.
 */
export const getDeliverableSources = async (
  id: string,
): Promise<DeliverableSourceRecord[]> => {
  try {
    const response = await http.get<DeliverableSourceRecord[]>(
      links.deliverable.sources(id),
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

/**
 * `PUT /deliverable/:id/source` — replaces the whole corpus.
 *
 * Called once, straight after a build's `POST /deliverable`. Send *every*
 * source that was inspected, including the ones the advisor unticked: a
 * document they excluded is still context a later edit may need, and this is
 * the only chance to store it.
 */
export const syncDeliverableSources = async (
  id: string,
  data: SyncDeliverableSourcesPayload,
): Promise<Deliverable> => {
  try {
    const response = await http.put<Deliverable>(
      links.deliverable.sources(id),
      data,
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

/**
 * `POST /deliverable/:id/source` — appends to the corpus.
 *
 * For files attached to a chat turn. Append rather than replace because the
 * page does not hold the stored corpus: sending the whole set back would mean
 * downloading megabytes only to return them, and a dropped response would cost
 * the advisor everything already stored. Re-sending a source already stored is
 * a no-op server-side, so a failed turn is safe to retry.
 */
export const appendDeliverableSources = async (
  id: string,
  data: SyncDeliverableSourcesPayload,
): Promise<Deliverable> => {
  try {
    const response = await http.post<Deliverable>(
      links.deliverable.sources(id),
      data,
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

/**
 * `PUT /deliverable/:id/asset` — replaces the branding set.
 *
 * Separate from the corpus sync so retrying a failed corpus write does not
 * re-upload the logo, and vice versa.
 */
export const syncDeliverableAssets = async (
  id: string,
  data: SyncDeliverableAssetsPayload,
): Promise<Deliverable> => {
  try {
    const response = await http.put<Deliverable>(
      links.deliverable.assets(id),
      data,
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};
